import { forwardRef, useRef, useState } from "react";
import { Link, type LinkProps, useLocation } from "react-router-dom";
import { requestDownload } from "../downloadApi";
import { startTrackedDownload } from "../downloadTracking";
import { detectDownloadTarget, readMacRenderer } from "../downloadPlatform";

/** Keep campaign/source parameters in the fallback URL, but resolve the installer in place. */
export const DownloadLink = forwardRef<HTMLAnchorElement, LinkProps>(function DownloadLink({ to, onClick, children, ...props }, ref) {
  const location = useLocation();
  const zh = location.pathname.startsWith("/cn/") || location.pathname.startsWith("/hk/");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  async function download() {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const detected = await detectDownloadTarget(navigator, readMacRenderer);
      if (typeof detected === "string") return;
      const url = new URL(typeof to === "string" ? to : `${to.pathname || ""}${to.search || ""}`, window.location.origin);
      const industry = /dental/i.test(location.pathname) ? "dental" : "other";
      const result = await requestDownload(industry, detected.platform, detected.architecture, zh ? "zh" : "en", url.searchParams);
      await startTrackedDownload(result.download_url);
    } catch {
      // Keep direct-download actions on the current page. The download API
      // records failures, so a transient backend error must not open a modal
      // or route visitors into a separate download flow.
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return <>
    <Link {...props} ref={ref} to={to} aria-busy={busy || undefined} aria-disabled={busy || undefined} onClick={event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      // Cancel React Router's link action before analytics or download work.
      // This control must only start the installer download and must never
      // navigate to an anchor or alter the page's scroll position.
      event.preventDefault();
      onClick?.(event);
      void download();
    }}>{children}</Link>
  </>;
});
